import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  fetchProfileRemote,
  type JainLexiconProfile,
  type StaffRole,
} from "@/lib/authApi";
import type { JainLexiconRbacState } from "@jl/lib/jainLexiconRbac";
import { toastApiError } from "@/lib/toastApiError";
import { UserCircle } from "lucide-react";

function formatStaffProfileRole(
  rbac: JainLexiconRbacState | null,
  staffRole: StaffRole | null
): { label: string; caption?: string } {
  const app = rbac?.appRole;
  if (app === "admin") {
    return { label: "Administrator", ...rbacCaption(rbac) };
  }
  if (app === "scholar") {
    return { label: "Scholar", ...rbacCaption(rbac) };
  }
  if (app === "contributor") {
    return { label: "Contributor", ...rbacCaption(rbac) };
  }

  const tut = rbac?.tenantUserType?.trim();
  if (tut === "tenant_admin") {
    return { label: "Administrator", caption: tut };
  }
  if (tut === "tenant_scholar") {
    return { label: "Scholar", caption: tut };
  }
  if (tut === "tenant_contributor") {
    return { label: "Contributor", caption: tut };
  }
  if (tut === "tenant_user") {
    return { label: "User", caption: tut };
  }

  const groupName =
    rbac?.permissionGroup?.name?.trim() ?? rbac?.authGroups?.[0]?.name?.trim();
  if (groupName) {
    const lower = groupName.toLowerCase();
    if (lower.includes("scholar")) {
      return { label: "Scholar", caption: groupName };
    }
    if (
      lower.includes("jain_lexicon_admin") ||
      (lower.includes("admin") && !lower.includes("scholar"))
    ) {
      return { label: "Administrator", caption: groupName };
    }
    if (lower.includes("contributer") || lower.includes("contributor")) {
      return { label: "Contributor", caption: groupName };
    }
    return { label: groupName };
  }

  if (staffRole === "admin") {
    return { label: "Administrator" };
  }
  if (staffRole === "scholar") {
    return { label: "Scholar" };
  }

  return { label: "—" };
}

function rbacCaption(
  rbac: JainLexiconRbacState | null
): { caption?: string } {
  const bits: string[] = [];
  const tut = rbac?.tenantUserType?.trim();
  if (tut) {
    bits.push(tut);
  }
  const g =
    rbac?.permissionGroup?.name?.trim() ??
    rbac?.authGroups?.[0]?.name?.trim();
  if (g && !bits.includes(g)) {
    bits.push(g);
  }
  return bits.length ? { caption: bits.join(" · ") } : {};
}

function row(label: string, value: string) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/60 py-2 last:border-b-0 last:pb-0">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="break-words text-right font-medium">{value}</span>
    </div>
  );
}

/** Staff profile overview from `/auth/profile/{user_id}/` using the staff JWT. */
export function AdminMyProfile() {
  const { staffUser, staffRole, staffRbac } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<JainLexiconProfile | null>(null);

  useEffect(() => {
    if (!staffUser?.id) {
      setLoading(false);
      setProfile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { profile: p, error } = await fetchProfileRemote(
        staffUser.id,
        "staff"
      );
      if (cancelled) {
        return;
      }
      setLoading(false);
      if (error) {
        toastApiError(error);
        setProfile(null);
        return;
      }
      setProfile(p);
    })();
    return () => {
      cancelled = true;
    };
  }, [staffUser?.id]);

  const email = profile?.email?.trim() || staffUser?.email?.trim() || "—";
  const first = profile?.first_name?.trim() || "—";
  const last = profile?.last_name?.trim() || "—";
  const full =
    profile?.full_name?.trim() ||
    [profile?.first_name?.trim(), profile?.last_name?.trim()]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "—";

  const { label: roleLabel, caption: roleCaption } = formatStaffProfileRole(
    staffRbac,
    staffRole
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-1 font-serif text-2xl font-bold">My profile</h2>
        
      </div>

      <Card className="max-w-lg border-border/80">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-full">
              <UserCircle className="text-primary h-6 w-6" />
            </div>
            <div>
              <CardTitle className="font-serif text-lg">Staff profile</CardTitle>
              <CardDescription>
                {loading
                  ? "Loading profile…"
                  : profile
                    ? ""
                    : "Using session fallback where needed"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-sm">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2 border-t-transparent" />
            </div>
          ) : (
            <>
              <div className="-mt-2 space-y-0">
                {row("Email", email)}
                {row("First name", first)}
                {row("Last name", last)}
                {row("Full name", full)}
              </div>
              <div className="border-border/60 mt-4 space-y-1 border-t pt-3">
                <div className="flex justify-between gap-4 text-sm">
                  <span className="text-muted-foreground">Lexicon role</span>
                  <span className="text-right font-bold text-foreground text-base">
                    {roleLabel}
                  </span>
                </div>
                
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
